import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { ProcessTemplateTaskType } from '@econtractor/types';
import TaskDueDateFields from '../../../shared/TaskDueDateFields';
import {
    applyEndDateChange,
    applyEndTimeChange,
    applyMultiDayChange,
    applyStartDateChange,
    applyStartTimeChange,
    applyTemplateTermDays,
    applyWithTimeChange,
    normalizeDueDateValues,
    type TaskDueDateValues,
} from '../../../shared/helpers/taskDueDateHelpers';
import { millisToTime } from '../../../NewTask/helpers/initNewTaskStruct';

export interface ProcessTaskDuePatch {
    dateStart: number;
    dateEnd: number;
    withTime: boolean;
    multiDay: boolean;
}

interface ProcessTaskDueDialogProps {
    show: boolean;
    task: ProcessTemplateTaskType;
    onHide: () => void;
    onSave: (patch: ProcessTaskDuePatch) => void;
}

const dueFromTask = (task: ProcessTemplateTaskType): TaskDueDateValues => {
    const now = DT.now().setZone('local');
    const savedEnd = Number(task.dateEnd) > 0;
    const savedStart = Number(task.dateStart) > 0;

    if (!savedEnd && !savedStart) {
        const fromTerm = applyTemplateTermDays(now.toMillis(), Number(task.termDays));
        if (fromTerm) return fromTerm;
    }

    const withTime = Boolean(task.withTime);
    const dateEnd = savedEnd ? Number(task.dateEnd) : now.endOf('day').toMillis();
    const dateStart = savedStart ? Number(task.dateStart) : now.startOf('day').toMillis();
    return {
        multiDay: Boolean(task.multiDay),
        withTime,
        dateStart,
        dateEnd,
        startTime: withTime ? millisToTime(dateStart, '00:00') : '00:00',
        endTime: withTime ? millisToTime(dateEnd, '18:00') : '18:00',
    };
};

const ProcessTaskDueDialog = ({ show, task, onHide, onSave }: ProcessTaskDueDialogProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const [values, setValues] = useState<TaskDueDateValues>(() => dueFromTask(task));

    useEffect(() => {
        if (!show) return;
        setValues(dueFromTask(task));
        // Лише в момент відкриття: далі форма живе локально, доки діалог відкритий.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const patch = (partial: Partial<TaskDueDateValues>) => {
        setValues((prev) => ({ ...prev, ...partial }));
    };

    const save = () => {
        const normalized = normalizeDueDateValues(values);
        onSave({
            dateStart: normalized.dateStart,
            dateEnd: normalized.dateEnd,
            withTime: normalized.withTime,
            multiDay: normalized.multiDay,
        });
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('processRuns.dueTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="text-body fw-medium mb-3">{task.taskName}</div>
                <TaskDueDateFields
                    idPrefix={`process-due-${task.dtId}`}
                    hideLabel
                    multiDay={values.multiDay}
                    withTime={values.withTime}
                    dateStart={values.dateStart}
                    dateEnd={values.dateEnd}
                    startTime={values.startTime}
                    endTime={values.endTime}
                    onMultiDayChange={(checked) => patch(applyMultiDayChange(values, checked))}
                    onWithTimeChange={(checked) => patch(applyWithTimeChange(values, checked))}
                    onStartDateChange={(iso) => patch(applyStartDateChange(values, iso))}
                    onEndDateChange={(iso) => patch(applyEndDateChange(values, iso))}
                    onStartTimeChange={(time) => patch(applyStartTimeChange(values, time))}
                    onEndTimeChange={(time) => patch(applyEndTimeChange(values, time))}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>
                    {t('common:buttons.cancel')}
                </Button>
                <Button variant="primary" onClick={save}>
                    {t('common:buttons.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProcessTaskDueDialog;
